import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink, ActivatedRoute, Router} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {PaginationComponent} from '../../../shared/components/pagination/pagination.component';
import {
  createPaginationState,
  setPaginationLoading,
  updatePaginationFromResult,
} from '../../../core/helpers/pagination-state.helper';
import {AuditService, AuditEventFilterModel} from '../../../core/services/audit/audit.service';
import {AuditEvent, auditFriendlyName} from '../../../core/models/audit/audit-event.model';
import {ROLE_AUDIT_READ} from '../../../core/models/user/user-info.model';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {Subscription} from 'rxjs';
import {formatElapsed} from '../../../core/helpers/date-timer.helper';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {FeatherPipe} from '../../../core/pipes/feather.pipe';

@Component({
  selector: 'app-audit-event-list',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule,
    PaginationComponent,
    AlertComponent,
    LoadingComponent,
    ButtonComponent,
    FeatherPipe
  ],
  templateUrl: './audit-event-list.component.html',
  styleUrl: './audit-event-list.component.scss'
})
export class AuditEventListComponent implements OnInit, OnDestroy {

  error: string | null = null;
  loading: boolean = false;
  version: 'v1' | 'v2' = 'v1';

  events: AuditEvent[] = [];
  readonly paginationState = createPaginationState();

  // UI filter model reflecting query params (dates as strings for query)
  uiFilters: { actionName: string; userName: string; email: string; startDate: string; endDate: string; success: string } = {
    actionName: '',
    userName: '',
    email: '',
    startDate: '',
    endDate: '',
    success: ''
  };

  private qpSub?: Subscription;
  private presetFromUrl?:string | null

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly auditService: AuditService,
    private readonly auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.version = this.auth.hasRole(ROLE_AUDIT_READ) ? 'v1' : 'v2';

    this.qpSub = this.route.queryParams.subscribe(params => {
      const page = parseInt(params['page'] ?? '0', 10);
      const pageSize = parseInt(params['pageSize'] ?? '10', 10);

      this.uiFilters.actionName = params['actionName'] ?? '';
      this.uiFilters.userName = params['userName'] ?? '';
      this.uiFilters.email = params['email'] ?? '';
      this.uiFilters.startDate = params['startDate'] ?? '';
      this.uiFilters.endDate = params['endDate'] ?? '';
      this.uiFilters.success = params['success'] ?? '';

      const type = params['type'];

      if (type === 'customer') {
        this.presetFromUrl = 'customer';
      } else if (type === 'seller') {
        this.presetFromUrl = 'seller';
      }

      this.fetchList(page, pageSize);
    });
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
  }

  private buildFilter(page: number, pageSize: number): AuditEventFilterModel {
    const filter: AuditEventFilterModel = { page, pageSize } as AuditEventFilterModel;

    if (this.version === 'v2') {
      if (this.presetFromUrl === 'customer') {
        filter.actionNames = ['RectifyCustomer'];
      }

      if (this.presetFromUrl === 'seller') {
        filter.actionNames = ['PutUpdateShopSellerVtexSync'];
      }
    }

    if (this.uiFilters.actionName?.trim()) filter.actionName = this.uiFilters.actionName.trim();
    if (this.uiFilters.userName?.trim()) filter.userName = this.uiFilters.userName.trim();
    if (this.uiFilters.email?.trim()) filter.email = this.uiFilters.email.trim();
    if (this.uiFilters.startDate?.trim()) filter.startDate = this.uiFilters.startDate.trim();
    if (this.uiFilters.endDate?.trim()) filter.endDate = this.uiFilters.endDate.trim();
    if (this.uiFilters.success === 'true') filter.success = true;
    if (this.uiFilters.success === 'false') filter.success = false;

    return filter;
  }

  private fetchList(page: number, pageSize: number): void {
    setPaginationLoading(this.paginationState, true);
    this.error = null;

    const filter = this.buildFilter(page, pageSize);

    this.auditService.getEvents(filter, this.version).subscribe({
      next: (result) => {
        this.events = result.content;
        updatePaginationFromResult(this.paginationState, result, pageSize);
      },
      error: (err) => {
        this.error = 'Falha ao carregar os dados.';
        setPaginationLoading(this.paginationState, false);
        console.error(err);
      }
    });
  }

  onPageChange = (page: number, pageSize: number): void => {
    this.navigateWithQuery({ page, pageSize });
  }

  onSearch(): void {
    this.navigateWithQuery({ page: 0, pageSize: this.paginationState().itemsPerPage });
  }

  onClear(): void {
    this.uiFilters = { actionName: '', userName: '', email: '', startDate: '', endDate: '', success: '' };
    this.navigateWithQuery({ page: 0, pageSize: 10 }, true);
  }

  private navigateWithQuery(pagination: { page: number; pageSize: number }, clearAll?: boolean): void {
    const baseParams: any = {
      page: pagination.page,
      pageSize: pagination.pageSize
    };

    const filterParams: any = clearAll ? {
      actionName: null,
      userName: null,
      email: null,
      startDate: null,
      endDate: null,
      success: null
    } : {
      actionName: this.uiFilters.actionName?.trim() ? this.uiFilters.actionName.trim() : null,
      userName: this.uiFilters.userName?.trim() ? this.uiFilters.userName.trim() : null,
      email: this.uiFilters.email?.trim() ? this.uiFilters.email.trim() : null,
      startDate: this.uiFilters.startDate?.trim() ? this.uiFilters.startDate.trim() : null,
      endDate: this.uiFilters.endDate?.trim() ? this.uiFilters.endDate.trim() : null,
      success: this.uiFilters.success ? this.uiFilters.success : null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...filterParams, ...baseParams },
      queryParamsHandling: 'merge'
    });
  }

  protected readonly formatElapsed = formatElapsed;
  protected readonly auditFriendlyName = auditFriendlyName;
}
